import { z } from 'zod';
import { optionalUrlSchema } from '@/lib/utils/url-schema.utils';
import { optionalWholeNumber } from '@/lib/utils/whole-number-schema.utils';
import { RAISE_RANGE_ERROR } from '@/features/startups/constants';
import { sectorOtherSchema, selfIdAnswerSchema, yesNoSchema } from '@/features/startups/schema';
import type {
  StartupProfileData,
  StartupFoundingTeamMember,
  StartupProfileUpdatePayload,
} from '../../api/startup-profile.api';

/**
 * Presence-check relaxation (MYN-127/131, client decision 2026-08-05):
 * `docs/reference/data-fields.md`'s Startup Profile Model table marks most
 * fields "Req", but its own preamble says that means "required for a
 * complete/matchable profile" for the Capital Raise matching algorithm - NOT
 * required to save the profile ("All fields optional for Startup Admins").
 * Only `companyName` blocks a save here (duplicate-domain detection, and
 * it's displayed on other screens) - see `features/startups/schema.ts` for
 * the wizard's identical relaxation and the same rationale in full: relaxing
 * only the create wizard would leave a trap where a founder who creates a
 * bare profile then opens Edit can't save anything until every field is
 * filled in. Format validation (URL/number/enum shape) still applies to any
 * value that IS supplied.
 */
const optionalUrl = optionalUrlSchema;

/** A checkbox group RHF reports as `false` when nothing is ticked - normalized to `[]`. */
const stringList = z.preprocess(
  (value) => (Array.isArray(value) ? value : []),
  z.array(z.string()),
);

const foundingTeamMemberSchema = z.object({
  name: z.string().trim().optional(),
  title: z.string().trim().optional(),
  linkedinUrl: optionalUrl,
  // The Past Exits control is a native <select> bound via register() with
  // 'yes'/'no' string option values (PAST_EXITS_OPTIONS) - it can never
  // produce a real boolean, so a plain z.boolean() here silently fails
  // validation on every submit (confirmed via Playwright: the select's DOM
  // value is '' until chosen, `handleSubmit`'s valid-callback then never
  // fires - no error surfaced, Save Changes just does nothing). Mirrors the
  // wizard's own foundingTeamMemberSchema (features/startups/schema.ts)
  // exactly: string enum at the form-values layer, converted to a real
  // boolean only in toApiPayload. Presence check dropped 2026-08-05 (see the
  // relaxation comment above) - a supplied value must still be 'yes'/'no'.
  pastExits: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['yes', 'no']).optional(),
  ),
  // True only for members hydrated from the fetched profile (set in
  // toFormFoundingTeamMember below). Lets FoundingTeamSection tell "already
  // saved, show a read-only summary row" apart from "just appended via +Add
  // Team Member, still needs the full form" - useFieldArray's `fields` don't
  // otherwise carry that distinction once rows are removed/reordered.
  isExisting: z.boolean().optional(),
});

export const startupProfileSchema = z
  .object({
    companyName: z.string().trim().min(1, 'Company name is required'),
    briefDescription: z.string().trim().optional(),
    companyWebsiteUrl: optionalUrl,
    companyLinkedinUrl: optionalUrl,
    // No `logoUrl` field. It used to be carried through form state purely to
    // round-trip the existing value on save. MYN-175 makes that harmful: the
    // Backend write DTO now takes `logoFileKey` (a raw S3 key) and `logoUrl` is
    // a read-only resolved presigned URL, so sending it back would both be
    // rejected under `forbidNonWhitelisted` and try to store a URL in a key
    // column. Omitting the field from a PATCH leaves it unchanged, which is
    // what the round-trip was for.
    //
    // MYN-101 adds the logo upload this note anticipated (`LogoSection`), and
    // binds it to `logoFileKey` as `UploadsLinks.tsx` does - never to
    // `logoUrl`. Three states, and they are all load-bearing: `undefined` =
    // untouched (never seeded from `logoUrl`, which is a presigned URL and not
    // a key), `''` = the Remove action's clear signal, a key = a fresh upload.
    // See `toFormValues`/`toApiPayload` below for how each is sent.
    logoFileKey: z.string().trim().optional(),
    sectorId: z.coerce.number().int().positive().optional(),
    subsectorIds: z.array(z.number()).max(2).optional(),
    sectorOther: sectorOtherSchema,
    fundingStage: z.string().optional(),
    yearFounded: optionalWholeNumber,
    employeeCount: optionalWholeNumber,
    hqCountryId: z.coerce.number().int().positive().optional(),
    hqStateId: z.coerce.number().int().positive().optional(),
    hqCityId: z.coerce.number().int().positive().optional(),
    hqCityName: z.string().optional(),
    totalRevenueRange: z.string().optional(),
    arr: optionalWholeNumber,
    amountRaisedToDate: optionalWholeNumber,
    nondilutiveFunding: optionalWholeNumber,
    recentMilestone: z.string().trim().optional(),
    financeSoughtLow: optionalWholeNumber,
    financeSoughtHigh: optionalWholeNumber,
    instrument: z.string().optional(),
    targetCloseDate: z.string().optional(),
    valuationOrCap: optionalWholeNumber,
    hasLeadInvestor: yesNoSchema,
    leadInvestorName: z.string().trim().optional(),
    pitchDeckFileKey: z.string().trim().optional(),
    pitchDeckFileSizeBytes: optionalWholeNumber,
    // A link, not an upload (A-58). Uses the same optionalUrl helper as every
    // other URL field so a typo is a field error, not a server 400. The
    // BACKEND additionally pins the scheme to http/https - that is the
    // boundary that matters, since this value ends up in an href on the
    // public shared profile.
    pitchVideoUrl: optionalUrl,
    marketModels: z.array(z.string()).optional(),
    foundingTeam: z.array(foundingTeamMemberSchema).max(5).optional(),
    // Parity with the create wizard (UX sheet r228).
    aiClassification: z.string().optional(),
    revenueModel: z.string().optional(),
    revenueGrowthRate: z.string().optional(),
    payingCustomers: z.string().optional(),
    useOfFunds: z.string().trim().optional(),
    investorTypesSought: stringList,
    founderFemale: selfIdAnswerSchema,
    founderUnderrepresented: selfIdAnswerSchema,
    founderUnderrepresentedCategories: stringList,
  })
  .refine(
    (values) =>
      values.financeSoughtLow == null ||
      values.financeSoughtHigh == null ||
      values.financeSoughtHigh >= values.financeSoughtLow,
    { message: RAISE_RANGE_ERROR, path: ['financeSoughtHigh'] },
  );

export type StartupProfileFormValues = z.infer<typeof startupProfileSchema>;

function toFormFoundingTeamMember(member: StartupFoundingTeamMember) {
  return {
    name: member.name,
    title: member.title,
    linkedinUrl: member.linkedinUrl ?? '',
    pastExits: (member.pastExits ? 'yes' : 'no') as 'yes' | 'no',
    isExisting: true,
  };
}

/** Maps the fetched profile onto the Edit form's default values. */
export function toFormValues(data: StartupProfileData): StartupProfileFormValues {
  const primarySector = data.sectors?.find((row) => row.isPrimary);
  const subsectorIds =
    data.sectors
      ?.filter((row) => !row.isPrimary && row.subsectorId != null)
      .map((row) => row.subsectorId as number) ?? [];

  return {
    companyName: data.companyName,
    briefDescription: data.briefDescription ?? '',
    companyWebsiteUrl: data.companyWebsiteUrl ?? '',
    companyLinkedinUrl: data.companyLinkedinUrl ?? '',
    sectorId: primarySector?.sectorId,
    subsectorIds,
    sectorOther: data.sectorOther ?? '',
    fundingStage: data.fundingStage ?? '',
    yearFounded: data.yearFounded ?? undefined,
    employeeCount: data.employeeCount ?? undefined,
    hqCountryId: data.hqCountryId ?? undefined,
    hqStateId: data.hqStateId ?? undefined,
    hqCityId: data.hqCityId ?? undefined,
    hqCityName: data.hqCity ?? '',
    totalRevenueRange: data.totalRevenueRange ?? '',
    arr: data.arr ?? undefined,
    amountRaisedToDate: data.amountRaisedToDate ?? undefined,
    nondilutiveFunding: data.nondilutiveFunding ?? undefined,
    recentMilestone: data.recentMilestone ?? '',
    financeSoughtLow: data.financeSoughtLow ?? undefined,
    financeSoughtHigh: data.financeSoughtHigh ?? undefined,
    instrument: data.instrument ?? '',
    targetCloseDate: data.targetCloseDate ?? '',
    valuationOrCap: data.valuationOrCap ?? undefined,
    hasLeadInvestor: data.hasLeadInvestor ? 'yes' : 'no',
    leadInvestorName: data.leadInvestorName ?? '',
    // Deliberately NOT seeded from `data.logoUrl`, unlike `pitchDeckFileKey`
    // below: `pitchDeckUrl` still holds the raw S3 key on read, but `logoUrl`
    // is a RESOLVED presigned URL (MYN-175), so seeding it here would round a
    // URL back into a key column. Left `undefined` - "untouched" - which
    // `toApiPayload` omits from the PATCH body, leaving the stored logo
    // exactly as it was. Only `LogoSection` ever sets this field.
    logoFileKey: undefined,
    pitchDeckFileKey: data.pitchDeckUrl ?? '',
    pitchDeckFileSizeBytes: data.pitchDeckFileSizeBytes ?? undefined,
    pitchVideoUrl: data.pitchVideoUrl ?? '',
    // Defensive filter: confirmed via live testing that an existing profile
    // can carry a null market-model entry (pre-existing data condition, root
    // cause not in this screen's scope - this field isn't even displayed in
    // the Edit form). Without the filter, a single null entry fails
    // startupProfileSchema's `z.array(z.string())` silently - Save Changes
    // does nothing, no error shown, for a field the user can't see or fix.
    marketModels: data.marketModels?.filter((m): m is string => Boolean(m)) ?? [],
    foundingTeam: data.foundingTeam?.map(toFormFoundingTeamMember) ?? [],
    aiClassification: data.aiClassification ?? '',
    revenueModel: data.revenueModel ?? '',
    revenueGrowthRate: data.revenueGrowthRate ?? '',
    payingCustomers: data.payingCustomers ?? '',
    useOfFunds: data.useOfFunds ?? '',
    investorTypesSought: data.investorTypesSought ?? [],
    founderFemale: data.founderFemale ?? undefined,
    founderUnderrepresented: data.founderUnderrepresented ?? undefined,
    founderUnderrepresentedCategories: data.founderUnderrepresentedCategories ?? [],
  };
}

/**
 * A field the founder emptied: `null` (the DTO's clear sentinel) when the saved
 * profile had a value, otherwise absent - so saving an untouched blank field
 * never writes anything.
 */
function clearable<T>(next: T | undefined, saved: T | null | undefined): T | null | undefined {
  if (next !== undefined) return next;
  return saved != null ? null : undefined;
}

/** Blank strings count as empty for `clearable`. */
function filled(value: string | undefined): string | undefined {
  return value?.trim() || undefined;
}

/** List version of `clearable`: `[]` (the DTO's clear sentinel) replaces a saved non-empty list. */
function clearableList(next: string[], saved: string[] | undefined): string[] | undefined {
  if (next.length > 0) return next;
  return saved?.length ? [] : undefined;
}

type Payload = StartupProfileUpdatePayload;

/**
 * HQ ids. Cleared levels send `null` so a stored value is not left behind. A
 * city picked with no state (non-US, UX sheet r105) omits the state entirely:
 * the Backend derives it from the city, so no stale or `null` state is sent.
 */
function hqLocationPayload(values: StartupProfileFormValues, saved: StartupProfileData): Payload {
  const stateDerivedFromCity = values.hqCityId !== undefined && values.hqStateId === undefined;
  return {
    hqCountryId: values.hqCountryId,
    hqStateId: stateDerivedFromCity ? undefined : clearable(values.hqStateId, saved.hqStateId),
    hqCityId: clearable(values.hqCityId, saved.hqCityId),
  };
}

/** An explicit Yes/No (UX sheet r151); "no" also clears any saved name. */
function leadInvestorPayload(values: StartupProfileFormValues, saved: StartupProfileData): Payload {
  const hasLeadInvestor = values.hasLeadInvestor === 'yes';
  return {
    hasLeadInvestor,
    leadInvestorName: hasLeadInvestor
      ? clearable(filled(values.leadInvestorName), saved.leadInvestorName)
      : null,
  };
}

/** Logo, pitch deck and pitch video - each with its own Backend clear sentinel. */
function uploadsPayload(values: StartupProfileFormValues): Payload {
  return {
    // The logo does NOT share the pitch deck's empty-string clear convention
    // below, because its Backend field is transformed differently:
    // `UpdateStartupProfileDto` runs `@Transform(emptyToUndefined)` on
    // `logoFileKey` (it does not on `pitchDeckFileKey`), so an `''` would
    // arrive as "leave unchanged" and Remove would silently do nothing. That
    // DTO's documented clear sentinel is `null`, so the form's `''` is
    // translated here. `undefined` (untouched - see `toFormValues`) is dropped
    // by `JSON.stringify` and leaves the stored logo alone.
    logoFileKey: values.logoFileKey === '' ? null : values.logoFileKey,
    // Deliberately NOT `|| undefined` like every other optional string field:
    // pitchDeckFileKey/pitchVideoUrl uniquely have "clear" semantics on the
    // Backend (UpdateStartupProfileDto has no `@Transform(emptyToUndefined)`
    // on these two) - an explicit '' means "clear it", a key genuinely absent
    // from the JSON body means "don't touch it". This is always a hydrated
    // Edit form (toFormValues seeds '' for "no file"), so the value is never
    // ambiguously "untouched" - sending it as-is is correct. `|| undefined`
    // here turned '' into `undefined`, which JSON.stringify drops from the
    // request body entirely, so clicking "Remove" was a silent no-op.
    pitchDeckFileKey: values.pitchDeckFileKey,
    pitchDeckFileSizeBytes: values.pitchDeckFileSizeBytes,
    pitchVideoUrl: values.pitchVideoUrl,
  };
}

/**
 * name/title are optional at the form-values layer (2026-08-05), so a member
 * row added via "+ Add Team Member" but never filled in is dropped rather than
 * sent with a blank name/title - mirrors the wizard's toCreatePayload.
 */
function foundingTeamPayload(values: StartupProfileFormValues): Payload['foundingTeam'] {
  return values.foundingTeam
    ?.filter((member) => Boolean(member.name?.trim()))
    .map((member) => ({
      name: (member.name ?? '').trim(),
      title: (member.title ?? '').trim(),
      linkedinUrl: member.linkedinUrl || undefined,
      pastExits: member.pastExits === 'yes',
      // No `photoUrl` (dropped, MYN-175) and no `bio` (removed from the
      // product, client UX sheet r127): the PATCH DTO's
      // forbidNonWhitelisted validation would reject an unknown key.
    }));
}

/** Fields added for parity with the create wizard (UX sheet r228) and self-identification. */
function parityPayload(values: StartupProfileFormValues, saved: StartupProfileData): Payload {
  return {
    aiClassification: clearable(filled(values.aiClassification), saved.aiClassification),
    revenueModel: clearable(filled(values.revenueModel), saved.revenueModel),
    revenueGrowthRate: clearable(filled(values.revenueGrowthRate), saved.revenueGrowthRate),
    payingCustomers: clearable(filled(values.payingCustomers), saved.payingCustomers),
    useOfFunds: clearable(filled(values.useOfFunds), saved.useOfFunds),
    investorTypesSought: clearableList(values.investorTypesSought, saved.investorTypesSought),
    founderFemale: clearable(values.founderFemale, saved.founderFemale),
    founderUnderrepresented: clearable(
      values.founderUnderrepresented,
      saved.founderUnderrepresented,
    ),
    // Categories only travel with a "yes" (FounderSelfIdentificationFields).
    founderUnderrepresentedCategories: clearableList(
      values.founderUnderrepresented === 'yes' ? values.founderUnderrepresentedCategories : [],
      saved.founderUnderrepresentedCategories,
    ),
  };
}

/**
 * Maps Edit form values onto the PATCH body (`StartupProfileUpdatePayload`,
 * NOT `StartupProfileData` - the read and write shapes genuinely differ for
 * this entity: flat `sectorId`/`subsectorIds` here vs. nested `sectors[]` on
 * read, `pitchDeckFileKey` here vs. `pitchDeckUrl` on read, and no `id`/
 * `entityId` - the Backend's `UpdateStartupProfileDto` doesn't declare them
 * and its `ValidationPipe({ forbidNonWhitelisted: true })` 400s on any field
 * it doesn't know about).
 */
export function toApiPayload(values: StartupProfileFormValues, saved: StartupProfileData): Payload {
  return {
    companyName: values.companyName.trim(),
    briefDescription: values.briefDescription?.trim() || undefined,
    companyWebsiteUrl: values.companyWebsiteUrl || undefined,
    companyLinkedinUrl: values.companyLinkedinUrl || undefined,
    sectorId: values.sectorId,
    subsectorIds: values.subsectorIds?.length ? values.subsectorIds : undefined,
    fundingStage: values.fundingStage || undefined,
    yearFounded: clearable(values.yearFounded, saved.yearFounded),
    ...hqLocationPayload(values, saved),
    // Only held while "Other" is the sector (SectorFields clears it on change).
    sectorOther: clearable(filled(values.sectorOther), saved.sectorOther),
    employeeCount: clearable(values.employeeCount, saved.employeeCount),
    totalRevenueRange: values.totalRevenueRange || undefined,
    arr: clearable(values.arr, saved.arr),
    amountRaisedToDate: clearable(values.amountRaisedToDate, saved.amountRaisedToDate),
    nondilutiveFunding: clearable(values.nondilutiveFunding, saved.nondilutiveFunding),
    recentMilestone: values.recentMilestone?.trim() || undefined,
    financeSoughtLow: clearable(values.financeSoughtLow, saved.financeSoughtLow),
    financeSoughtHigh: clearable(values.financeSoughtHigh, saved.financeSoughtHigh),
    instrument: values.instrument || undefined,
    targetCloseDate: values.targetCloseDate || undefined,
    valuationOrCap: clearable(values.valuationOrCap, saved.valuationOrCap),
    ...leadInvestorPayload(values, saved),
    ...uploadsPayload(values),
    marketModels: values.marketModels?.length ? values.marketModels : undefined,
    foundingTeam: foundingTeamPayload(values),
    ...parityPayload(values, saved),
  };
}
